import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { loadActiveAssignmentsForBusiness } from 'store/assignments/thunks/loadActiveAssignmentsForBusiness';
import { loadAssignmentCostsForProject } from 'store/assignments/thunks/loadAssignmentCostsForProject';
import { createProject } from 'store/projects/thunks/createProject.thunk';
import {
  addFilter,
  clearExpensesTrackingData,
  clearExpenseTrackingForDateChange,
  getProjectAssignmentTree,
  removeFilter,
  resetFilters,
  setAllProjectAssignmentsLoaded,
  setAllProjectAssignmentsLoadedAmount,
  setFilters,
  updateAssignmentsFromGraphqlMutate,
} from './actions/expenses-tracking.actions';
import { loadCompanies } from './thunks/loadCompanies';
import { updateCompanyName } from './thunks/updateCompanyName.thunk';
import { loadCompanyInvoiceTotals } from './thunks/loadCompanyInvoiceTotals';
import { loadParentAndSubProjects } from './thunks/loadParentAndSubProjects';
import { loadResourceTree } from './thunks/loadResourceTree';
import { loadSettings } from './thunks/loadSettings';
import { saveSettings } from './thunks/saveSettings.thunk';
import { updateResource } from './thunks/updateResource';
import { updateResources } from './thunks/updateResources';
import { createAdditionalWork } from './thunks/createAdditionalWork.thunk';
import { deleteAdditionalWork } from './thunks/deleteAdditionalWork.thunk';
import { loadAdditionalWorks } from './thunks/loadAdditionalWorks';
import { updateAdditionalWork } from './thunks/updateAdditionalWork.thunk';
import { generateCostReport } from './thunks/generateCostReport.thunk';
import { loadCostReports } from './thunks/loadCostReports';
import { setCostReportStatus } from './thunks/setCostReportStatus.thunk';
import { saveCostReportSelection } from './thunks/saveCostReportSelection.thunk';

const resourceTreeAdapter = createEntityAdapter();
const costReportsAdapter = createEntityAdapter({
  selectId: (report) => report.PK,
});
const parentAndSubProjectsAdapter = createEntityAdapter();
const projectAssignmentsAdapter = createEntityAdapter();
const assignmentCostsAdapter = createEntityAdapter();
const companyTotalsAdapter = createEntityAdapter();
const companiesAdapter = createEntityAdapter();
const employerAdapter = createEntityAdapter();
const resourceAdapter = createEntityAdapter();
const recordAdapter = createEntityAdapter();
const additionalWorksAdapter = createEntityAdapter();
const resetState = {
  resourceTree: resourceTreeAdapter.getInitialState(),
  parentAndSubProjects: parentAndSubProjectsAdapter.getInitialState(),
  loadingResourceTree: false,
  projectAssignments: projectAssignmentsAdapter.getInitialState(),
  assignmentCosts: assignmentCostsAdapter.getInitialState(),
  companyInvoiceTotals: companyTotalsAdapter.getInitialState(),
  companies: companiesAdapter.getInitialState(),
  loadingCompanies: false,
  loadingUpdateCompanyName: false,
  settings: {},
  loadingCompanyInvoiceTotals: false,
  allProjectAssignmentsLoaded: false,
  allProjectAssignmentsLoadedAmount: 0,
  activeFilters: [],

  employers: employerAdapter.getInitialState(),
  resources: resourceAdapter.getInitialState(),
  records: recordAdapter.getInitialState(),
  additionalWorks: additionalWorksAdapter.getInitialState(),
  loadingCreateAdditionalWork: false,
  loadingAdditionalWorks: false,
  loadingUpdateAdditionalWork: false,
  loadingDeleteAdditionalWork: false,

  costReports: costReportsAdapter.getInitialState(),
  loadingCostReports: false,
  loadingGenerateCostReport: false,
  loadingSetCostReportStatus: false,
};

export const expenseTrackingSlice = createSlice({
  name: 'expenseTracking',
  initialState: {
    resourceTree: resourceTreeAdapter.getInitialState(),
    parentAndSubProjects: parentAndSubProjectsAdapter.getInitialState(),
    loadingResourceTree: false,
    projectAssignments: projectAssignmentsAdapter.getInitialState(),
    assignmentCosts: assignmentCostsAdapter.getInitialState(),
    companyInvoiceTotals: companyTotalsAdapter.getInitialState(),
    companies: companiesAdapter.getInitialState(),
    loadingCompanies: false,
    loadingUpdateCompanyName: false,
    settings: {},
    loadingCompanyInvoiceTotals: false,
    allProjectAssignmentsLoaded: false,
    allProjectAssignmentsLoadedAmount: 0,
    activeFilters: [],

    employers: employerAdapter.getInitialState(),
    resources: resourceAdapter.getInitialState(),
    records: recordAdapter.getInitialState(),
    additionalWorks: additionalWorksAdapter.getInitialState(),
    loadingCreateAdditionalWork: false,
    loadingAdditionalWorks: false,
    loadingUpdateAdditionalWork: false,
    loadingDeleteAdditionalWork: false,

    costReports: costReportsAdapter.getInitialState(),
    loadingCostReports: false,
    loadingGenerateCostReport: false,
    loadingSetCostReportStatus: false,
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadResourceTree.pending, (state) => ({
        ...state,
        loadingResourceTree: true,
      }))
      .addCase(loadResourceTree.fulfilled, (state, action) => {
        // resourceTreeAdapter.setAll(state.resourceTree, action.payload);
        resourceAdapter.setAll(state.resources, action.payload.resources);
        employerAdapter.setAll(state.employers, action.payload.employers);
        state.loadingResourceTree = false;
      })
      .addCase(loadResourceTree.rejected, (state, action) => ({
        ...state,
        loadingResourceTree: false,
        error: action.error,
      }))
      // .addCase(loadResourcesOfProjectWithEmployerData.pending, (state) => ({
      //   ...state,
      //   loadingResourceTree: true,
      // }))
      // .addCase(loadResourcesOfProjectWithEmployerData.fulfilled, (state, action) => {
      //   resourceAdapter.setAll(state.resources, action.payload.resources);
      //   employerAdapter.setAll(state.employers, action.payload.employerProjects);

      //   state.loadingResourceTree = false;
      // })
      // .addCase(loadResourcesOfProjectWithEmployerData.rejected, (state, action) => ({
      //   ...state,
      //   loadingResourceTree: false,
      //   error: action.error,
      // }))
      .addCase(loadSettings.pending, (state) => ({
        ...state,
      }))
      .addCase(loadSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      .addCase(loadSettings.rejected, (state, action) => ({
        ...state,
        error: action.error,
      }))
      .addCase(saveSettings.pending, (state) => ({
        ...state,
      }))
      .addCase(saveSettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      .addCase(saveSettings.rejected, (state, action) => ({
        ...state,
        error: action.error,
      }))
      .addCase(loadCompanies.pending, (state) => ({
        ...state,
        loadingCompanies: true,
      }))
      .addCase(loadCompanies.fulfilled, (state, action) => {
        companiesAdapter.setAll(state.companies, action.payload || []);
        state.loadingCompanies = false;
      })
      .addCase(loadCompanies.rejected, (state, action) => ({
        ...state,
        loadingCompanies: false,
        error: action.error,
      }))
      .addCase(updateCompanyName.pending, (state) => ({
        ...state,
        loadingUpdateCompanyName: true,
      }))
      .addCase(updateCompanyName.fulfilled, (state, action) => {
        if (action.payload && action.payload.id) {
          companiesAdapter.upsertOne(state.companies, action.payload);
        }
        state.loadingUpdateCompanyName = false;
      })
      .addCase(updateCompanyName.rejected, (state, action) => ({
        ...state,
        loadingUpdateCompanyName: false,
        error: action.error,
      }))
      .addCase(loadCompanyInvoiceTotals.fulfilled, (state, action) => {
        state.companyInvoiceTotals = action.payload;
        state.loadingCompanyInvoiceTotals = false;
      })
      .addCase(loadCompanyInvoiceTotals.pending, (state) => ({
        ...state,
        loadingCompanyInvoiceTotals: true,
      }))
      .addCase(loadCompanyInvoiceTotals.rejected, (state, action) => ({
        ...state,
        error: action.error,
        loadingCompanyInvoiceTotals: false,
      }))

      .addCase(loadParentAndSubProjects.pending, (state) => ({
        ...state,
        loadingParentAndSubProjects: true,
      }))
      .addCase(loadParentAndSubProjects.fulfilled, (state, action) => {
        parentAndSubProjectsAdapter.setAll(
          state.parentAndSubProjects,
          action.payload
        );
        state.loadingParentAndSubProjects = false;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        parentAndSubProjectsAdapter.addOne(
          state.parentAndSubProjects,
          action.payload.newProject
        );
      })
      .addCase(loadParentAndSubProjects.rejected, (state, action) => ({
        ...state,
        loadingParentAndSubProjects: false,
        error: action.error,
      }))
      .addCase(updateResource.pending, (state) => ({
        ...state,
        loadingUpdateResources: true,
      }))
      .addCase(updateResource.fulfilled, (state, action) => {
        resourceAdapter.upsertOne(state.resources, action.payload);
        state.loadingUpdateResources = false;
      })
      .addCase(updateResource.rejected, (state, action) => ({
        ...state,
        loadingUpdateResources: false,
        error: action.error,
      }))
      .addCase(updateResources.pending, (state) => ({
        ...state,
        loadingUpdateResources: true,
      }))
      .addCase(updateResources.fulfilled, (state, action) => {
        console.log('updateResources.fulfilled', action.payload);
        resourceAdapter.upsertMany(state.resources, action.payload);
        state.loadingUpdateResources = false;
      })
      .addCase(updateResources.rejected, (state, action) => ({
        ...state,
        loadingUpdateResources: false,
        error: action.error,
      }))
      .addCase(createAdditionalWork.pending, (state) => ({
        ...state,
        loadingCreateAdditionalWork: true,
      }))
      .addCase(createAdditionalWork.fulfilled, (state, action) => {
        additionalWorksAdapter.addOne(state.additionalWorks, action.payload);
        state.loadingCreateAdditionalWork = false;
      })
      .addCase(createAdditionalWork.rejected, (state, action) => ({
        ...state,
        loadingCreateAdditionalWork: false,
        error: action.error,
      }))
      .addCase(loadAdditionalWorks.pending, (state) => ({
        ...state,
        loadingAdditionalWorks: true,
      }))
      .addCase(loadAdditionalWorks.fulfilled, (state, action) => {
        additionalWorksAdapter.setAll(state.additionalWorks, action.payload);
        state.loadingAdditionalWorks = false;
      })
      .addCase(loadAdditionalWorks.rejected, (state, action) => ({
        ...state,
        loadingAdditionalWorks: false,
        error: action.error,
      }))
      .addCase(updateAdditionalWork.pending, (state) => ({
        ...state,
        loadingUpdateAdditionalWork: true,
      }))
      .addCase(updateAdditionalWork.fulfilled, (state, action) => {
        additionalWorksAdapter.upsertOne(state.additionalWorks, action.payload);
        state.loadingUpdateAdditionalWork = false;
      })
      .addCase(updateAdditionalWork.rejected, (state, action) => ({
        ...state,
        loadingUpdateAdditionalWork: false,
        error: action.error,
      }))
      .addCase(deleteAdditionalWork.pending, (state) => ({
        ...state,
        loadingDeleteAdditionalWork: true,
      }))
      .addCase(deleteAdditionalWork.fulfilled, (state, action) => {
        additionalWorksAdapter.removeOne(state.additionalWorks, action.payload);
        state.loadingDeleteAdditionalWork = false;
      })
      .addCase(deleteAdditionalWork.rejected, (state, action) => ({
        ...state,
        loadingDeleteAdditionalWork: false,
        error: action.error,
      }))
      .addCase(loadCostReports.pending, (state) => ({
        ...state,
        loadingCostReports: true,
      }))
      .addCase(loadCostReports.fulfilled, (state, action) => {
        costReportsAdapter.setAll(state.costReports, action.payload);
        state.loadingCostReports = false;
      })
      .addCase(loadCostReports.rejected, (state, action) => ({
        ...state,
        loadingCostReports: false,
        error: action.error,
      }))
      .addCase(generateCostReport.pending, (state) => ({
        ...state,
        loadingGenerateCostReport: true,
      }))
      .addCase(generateCostReport.fulfilled, (state, action) => {
        costReportsAdapter.upsertOne(state.costReports, action.payload);
        state.loadingGenerateCostReport = false;
      })
      .addCase(generateCostReport.rejected, (state, action) => ({
        ...state,
        loadingGenerateCostReport: false,
        error: action.error,
      }))
      .addCase(setCostReportStatus.pending, (state) => ({
        ...state,
        loadingSetCostReportStatus: true,
      }))
      .addCase(setCostReportStatus.fulfilled, (state, action) => {
        if (action.payload && action.payload.PK) {
          costReportsAdapter.upsertOne(state.costReports, action.payload);
        }
        state.loadingSetCostReportStatus = false;
      })
      .addCase(setCostReportStatus.rejected, (state, action) => ({
        ...state,
        loadingSetCostReportStatus: false,
        error: action.error,
      }))
      .addCase(saveCostReportSelection.fulfilled, (state, action) => {
        if (action.payload && action.payload.PK) {
          costReportsAdapter.upsertOne(state.costReports, action.payload);
        }
      })
      .addCase(loadActiveAssignmentsForBusiness.pending, (state) => ({
        ...state,
        loadingProjectAssignments: true,
      }))
      .addCase(loadActiveAssignmentsForBusiness.fulfilled, (state, action) => {
        projectAssignmentsAdapter.setMany(
          state.projectAssignments,
          action.payload
        );
        state.loadingProjectAssignments = false;
      })
      .addCase(loadActiveAssignmentsForBusiness.rejected, (state, action) => ({
        ...state,
        loadingProjectAssignments: false,
        error: action.error,
      }))
      .addCase(loadAssignmentCostsForProject.pending, (state) => ({
        ...state,
        loadingProjectAssignments: true,
      }))
      .addCase(loadAssignmentCostsForProject.fulfilled, (state, action) => {
        assignmentCostsAdapter.setMany(state.assignmentCosts, action.payload);
        state.loadingProjectAssignments = false;
      })
      .addCase(loadAssignmentCostsForProject.rejected, (state, action) => ({
        ...state,
        loadingProjectAssignments: false,
        error: action.error,
      }))
      .addCase(getProjectAssignmentTree, (state, action) => ({
        ...state,
        projectAssignmentTree: action.payload,
      }))
      .addCase(setAllProjectAssignmentsLoaded, (state, action) => ({
        ...state,
        allProjectAssignmentsLoaded: action.payload,
      }))
      .addCase(setAllProjectAssignmentsLoadedAmount, (state, action) => ({
        ...state,
        allProjectAssignmentsLoadedAmount: action.payload,
      }))
      .addCase(clearExpensesTrackingData, () => resetState)
      .addCase(clearExpenseTrackingForDateChange, (state) => {
        state.resourceTree = resourceTreeAdapter.getInitialState();
      })
      .addCase(updateAssignmentsFromGraphqlMutate, (state, action) => {
        console.log('updateAssignmentsFromGraphqlMutate', action);
        if (action.payload.length) {
          projectAssignmentsAdapter.upsertMany(
            state.projectAssignments,
            action.payload
          );
        } else {
          projectAssignmentsAdapter.upsertOne(
            state.projectAssignments,
            action.payload
          );
        }
      })
      .addCase(setFilters, (state, action) => ({
        ...state,
        activeFilters: action.payload,
      }))
      .addCase(addFilter, (state, action) => ({
        ...state,
        activeFilters: [...state.activeFilters, action.payload],
      }))
      .addCase(removeFilter, (state, action) => ({
        ...state,
        activeFilters: state.activeFilters.filter(
          (filter) => filter !== action.payload
        ),
      }))
      .addCase(resetFilters, (state) => ({
        ...state,
        activeFilters: [],
      }));
  },
});
